// View a single student (full page with Back to list).
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/format';
import { showErrorToast } from '../../utils/errorHandler';
import { getStudent, RosterStudent } from '../../modules/roster/api/roster.client';
import { ROUTES } from '../../utils/constants';
import { ArrowLeft } from 'lucide-react';

interface EnrollmentWithClassSection {
  academic_year_key?: string;
  status?: string;
  effective_from?: string | null;
  effective_to?: string | null;
  class?: { name?: string; grade?: string };
  section?: { name?: string } | null;
}

interface StudentDetailResponse {
  student: RosterStudent;
  current_enrollment?: EnrollmentWithClassSection | null;
  enrollments?: EnrollmentWithClassSection[];
  grade?: { label?: string };
  parents?: Array<{ profile?: unknown; user?: unknown }> | null;
}

type StudentDetailData = RosterStudent | StudentDetailResponse;

function isDetailResponse(d: StudentDetailData): d is StudentDetailResponse {
  return d != null && 'student' in d && typeof (d as StudentDetailResponse).student === 'object';
}

function field(value: string | null | undefined): string {
  return value != null && String(value).trim() !== '' ? String(value).trim() : '-';
}

function getFullName(s: RosterStudent) {
  return `${s.first_name || ''} ${s.last_name || ''}`.trim() || '-';
}

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-wrap gap-x-2 text-sm">
    <span className="font-medium text-gray-500 dark:text-gray-400 shrink-0">{label}:</span>
    <span className="text-gray-900 dark:text-white">{value}</span>
  </div>
);

export const StudentViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<StudentDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getStudent(id)
      .then((data) => setDetail(data as StudentDetailData))
      .catch((err) => {
        showErrorToast(err);
        setDetail(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const goBack = () => navigate(ROUTES.ADMIN_STUDENTS);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!detail) {
    return (
      <Layout>
        <PageHeader title="Student" subtitle="Not found" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Students
          </Button>
        </div>
      </Layout>
    );
  }

  const student = isDetailResponse(detail) ? detail.student : detail;
  const displayName = student.display_name || getFullName(student);
  const currentEnrollment = isDetailResponse(detail) ? detail.current_enrollment : undefined;
  const enrollments = isDetailResponse(detail) ? detail.enrollments ?? [] : [];
  const gradeLabel = isDetailResponse(detail) ? detail.grade?.label : undefined;
  const parents = isDetailResponse(detail) ? detail.parents : undefined;

  const inviteStatus =
    student.accepted_at != null && String(student.accepted_at).trim() !== ''
      ? 'Joined'
      : student.invited_at != null && String(student.invited_at).trim() !== ''
        ? 'Invited'
        : 'Not invited';

  return (
    <Layout>
      <PageHeader
        title={displayName}
        subtitle="View enrolled student"
        actions={
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Students
          </Button>
        }
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent padding="md">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Student Profile</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Row label="Admission No" value={field(student.admission_no)} />
                <Row label="Full Name" value={getFullName(student)} />
                <Row label="Gender" value={field(student.gender)} />
                <Row label="DOB" value={student.dob ? formatDate(student.dob) : '-'} />
                <Row label="Phone" value={field(student.phone)} />
                <Row label="Email" value={field(student.email)} />
                <Row label="Address" value={field(student.address)} />
                <Row label="Status" value={field(student.status)} />
                {student.roll_no != null && String(student.roll_no).trim() !== '' && (
                  <Row label="Roll No" value={String(student.roll_no)} />
                )}
                {student.guardian_name != null && String(student.guardian_name).trim() !== '' && (
                  <Row label="Guardian Name" value={String(student.guardian_name)} />
                )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardContent padding="md">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Current Enrollment</h3>
              {currentEnrollment ? (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <Row label="Academic Year" value={field(currentEnrollment.academic_year_key)} />
                  <Row label="Class" value={field(currentEnrollment.class?.name)} />
                  <Row label="Grade" value={field(gradeLabel ?? currentEnrollment.class?.grade)} />
                  <Row
                    label="Section"
                    value={currentEnrollment.section?.name != null ? currentEnrollment.section.name : '-'}
                  />
                  <Row label="Enrollment Status" value={field(currentEnrollment.status)} />
                </dl>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No current enrollment</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent padding="md">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Enrollment History</h3>
            {enrollments.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No enrollment records</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Academic Year</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrollments.map((en, i) => (
                      <TableRow key={i}>
                        <TableCell>{field(en.academic_year_key)}</TableCell>
                        <TableCell>{field(en.class?.name)}</TableCell>
                        <TableCell>{en.section?.name ?? '-'}</TableCell>
                        <TableCell>{field(en.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-300">Invitation status:</span>
          <span className="text-gray-900 dark:text-white">{inviteStatus}</span>
          {student.invited_at && (
            <span className="text-gray-500 dark:text-gray-400">
              Invited at: {formatDate(student.invited_at)}
            </span>
          )}
          {student.accepted_at && (
            <span className="text-gray-500 dark:text-gray-400">
              Accepted at: {formatDate(student.accepted_at)}
            </span>
          )}
        </div>

        {parents && parents.length > 0 && (
          <Card>
            <CardContent padding="md">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Parents linked: {parents.length}
              </h3>
              {parents.every((p) => p.profile == null && p.user == null) ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Parent details not available yet</p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Parent information on file</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};
